// Land Intelligence Pipeline Manager — Kanban dashboard.
// Reads from /api/land/pipeline; advance stages via /api/land/parcels/{id}/stage.
// Brain: [skills/land-intelligence].
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || '';

const PIPELINE_STAGES = [
  { id: 'discovered', label: 'Discovered', accent: 'border-zinc-300 bg-zinc-50' },
  { id: 'screening', label: 'Screening', accent: 'border-sky-300 bg-sky-50' },
  { id: 'qualified', label: 'Qualified', accent: 'border-indigo-300 bg-indigo-50' },
  { id: 'diligence', label: 'Diligence', accent: 'border-violet-300 bg-violet-50' },
  { id: 'loi', label: 'LOI', accent: 'border-amber-300 bg-amber-50' },
  { id: 'contract', label: 'Contract', accent: 'border-emerald-300 bg-emerald-50' },
  { id: 'closed', label: 'Closed', accent: 'border-emerald-400 bg-emerald-100' },
  { id: 'passed', label: 'Passed', accent: 'border-zinc-400 bg-zinc-100 text-zinc-500' },
];

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function LandPipeline() {
  const [parcels, setParcels] = useState([]);
  const [aggregate, setAggregate] = useState(null);
  const [discovery, setDiscovery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [draggedId, setDraggedId] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // The portfolio endpoint returns ParcelSummary list; dashboard
      // needs the full list filterable by stage. Use /parcels for full
      // detail and /pipeline for the aggregate metrics. /discovery/recent
      // backs the self-stocking banner.
      const [parcelsResp, pipelineResp, discoveryResp] = await Promise.all([
        fetch(`${API_BASE}/land/parcels?limit=500`, { headers: authHeaders() }),
        fetch(`${API_BASE}/land/pipeline`, { headers: authHeaders() }),
        fetch(`${API_BASE}/land/discovery/recent`, { headers: authHeaders() }),
      ]);
      if (!parcelsResp.ok) throw new Error(`parcels: ${parcelsResp.status}`);
      if (!pipelineResp.ok) throw new Error(`pipeline: ${pipelineResp.status}`);
      const parcelsBody = await parcelsResp.json();
      const pipelineBody = await pipelineResp.json();
      // Discovery endpoint is best-effort — older deploys won't have it;
      // banner is a strip-friction signal, not a load-bearing feature.
      let discoveryBody = null;
      if (discoveryResp.ok) {
        discoveryBody = await discoveryResp.json();
      }
      setParcels(parcelsBody.parcels || []);
      setAggregate(pipelineBody);
      setDiscovery(discoveryBody);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const byStage = useMemo(() => {
    const groups = Object.fromEntries(PIPELINE_STAGES.map((s) => [s.id, []]));
    for (const p of parcels) {
      const stage = p.pipeline_stage || 'discovered';
      if (!groups[stage]) groups[stage] = [];
      groups[stage].push(p);
    }
    // Sort each column by speed_to_mw_months ascending (nulls last)
    for (const k of Object.keys(groups)) {
      groups[k].sort((a, b) => {
        const av = a.speed_to_mw_months ?? Number.POSITIVE_INFINITY;
        const bv = b.speed_to_mw_months ?? Number.POSITIVE_INFINITY;
        return av - bv;
      });
    }
    return groups;
  }, [parcels]);

  const advanceStage = async (parcelId, newStage) => {
    try {
      const resp = await fetch(`${API_BASE}/land/parcels/${parcelId}/stage`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ pipeline_stage: newStage }),
      });
      if (!resp.ok) throw new Error(`stage update: ${resp.status}`);
      // Optimistic update
      setParcels((prev) =>
        prev.map((p) => (p.parcel_id === parcelId ? { ...p, pipeline_stage: newStage } : p)),
      );
    } catch (e) {
      setError(e.message || String(e));
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 p-6 lg:p-10">
      <div className="mx-auto max-w-[1600px]">
        <header className="mb-6 flex items-end justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
              Land Intelligence — Pipeline Manager
            </p>
            <h1 className="mt-1 text-3xl font-bold tracking-tight text-zinc-900">
              Parcel Pipeline
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-zinc-600">
              Drag a parcel card to advance its pipeline stage. Click a card
              to open the full Parcel Detail (Power matrix, Cooling matrix,
              Deal Economics, enrichment history).
            </p>
          </div>
          <div className="flex gap-2">
            <Link
              to="/land-intelligence/alerts"
              className="rounded-xl border border-zinc-300 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 shadow-sm transition-colors hover:bg-zinc-100"
            >
              Alerts
            </Link>
            <Link
              to="/land-intelligence/map"
              className="rounded-xl border border-zinc-300 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 shadow-sm transition-colors hover:bg-zinc-100"
            >
              Map
            </Link>
            <Link
              to="/land-intelligence/ahp"
              className="rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800 shadow-sm transition-colors hover:bg-emerald-100"
              title="AHP decision support — sensitivity slider"
            >
              Rank (AHP)
            </Link>
            <Link
              to="/land-intelligence/stage-rules"
              className="rounded-xl border border-zinc-300 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 shadow-sm transition-colors hover:bg-zinc-100"
              title="Auto-stage advancement threshold rules (Batch 15.2)"
            >
              Stage rules
            </Link>
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="rounded-xl bg-zinc-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-zinc-700"
            >
              + Add parcel
            </button>
          </div>
        </header>

        {discovery && <DiscoveryBanner discovery={discovery} />}

        {aggregate && <AggregateBar agg={aggregate} parcels={parcels} />}

        {error && (
          <div className="mb-4 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        {loading && parcels.length === 0 ? (
          <div className="rounded-2xl border border-zinc-200 bg-white p-10 text-center text-sm text-zinc-500">
            Loading pipeline…
          </div>
        ) : parcels.length === 0 ? (
          <EmptyState onAdd={() => setShowAdd(true)} />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
            {PIPELINE_STAGES.map((stage) => (
              <KanbanColumn
                key={stage.id}
                stage={stage}
                parcels={byStage[stage.id] || []}
                onDrop={(parcelId) => advanceStage(parcelId, stage.id)}
                draggedId={draggedId}
                setDraggedId={setDraggedId}
              />
            ))}
          </div>
        )}

        {showAdd && (
          <AddParcelModal
            onClose={() => setShowAdd(false)}
            onCreated={() => {
              setShowAdd(false);
              refresh();
            }}
          />
        )}
      </div>
    </div>
  );
}

function DiscoveryBanner({ discovery }) {
  const totalNew = (discovery.totals?.inserted || 0) + (discovery.totals?.stage_advanced || 0);
  const totalRefreshed = discovery.totals?.updated || 0;
  const counties = discovery.counties_scanned || 0;
  const totalAutoPromoted = discovery.totals?.auto_advanced_by_rules || 0;
  const mostRecent = discovery.most_recent_run_at;

  // Only show banner when something happened in the window. Quiet days
  // shouldn't add visual noise.
  if (totalNew === 0 && totalRefreshed === 0 && totalAutoPromoted === 0) {
    return null;
  }

  const formatRel = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const hoursAgo = Math.round((Date.now() - d.getTime()) / 3_600_000);
    if (hoursAgo < 1) return 'within the last hour';
    if (hoursAgo < 24) return `${hoursAgo}h ago`;
    return `${Math.round(hoursAgo / 24)}d ago`;
  };

  const summary = totalNew > 0
    ? `${totalNew} new parcel${totalNew === 1 ? '' : 's'} discovered overnight from ${counties} count${counties === 1 ? 'y' : 'ies'} GIS`
    : (totalRefreshed > 0
      ? `${totalRefreshed} parcel${totalRefreshed === 1 ? '' : 's'} refreshed from ${counties} count${counties === 1 ? 'y' : 'ies'} GIS`
      : 'Auto-stage advancement ran overnight'
    );

  return (
    <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
            Pipeline self-stocking
          </p>
          <p className="mt-1 text-sm font-medium text-emerald-900">
            {summary}
          </p>
          <p className="mt-1 text-xs text-emerald-700">
            Last scan {formatRel(mostRecent)}.
            {totalNew > 0 && totalRefreshed > 0
              ? ` ${totalRefreshed} additional parcel${totalRefreshed === 1 ? '' : 's'} refreshed.`
              : ''}
            {totalAutoPromoted > 0 && (
              <>
                {' '}{totalAutoPromoted} parcel{totalAutoPromoted === 1 ? '' : 's'} auto-advanced
                {' '}per current threshold rules —
                {' '}<Link to="/land-intelligence/stage-rules" className="font-semibold underline">
                  review or adjust thresholds
                </Link>.
              </>
            )}
          </p>
        </div>
        {(discovery.runs || []).length > 0 && (
          <div className="text-right text-xs text-emerald-700">
            <div className="font-semibold">By county</div>
            {(discovery.runs || []).slice(0, 4).map((run, idx) => (
              <div key={`${run.county}-${idx}`}>
                {run.county}: {(run.inserted || 0) + (run.stage_advanced || 0)} new,
                {' '}{run.updated || 0} updated
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AggregateBar({ agg, parcels }) {
  const findParcel = (id) => parcels.find((p) => p.parcel_id === id);
  const fastest = findParcel(agg.fastest_to_mw);
  const lowestCost = findParcel(agg.lowest_cost);
  const highestRev = findParcel(agg.highest_revenue_acceleration);

  return (
    <div className="mb-6 grid gap-3 rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm sm:grid-cols-2 lg:grid-cols-4">
      <Stat label="Total parcels" value={agg.total_parcels_in_system} />
      <Stat
        label="Fastest to MW"
        value={fastest ? `${fastest.parcel_id}` : '—'}
        sub={fastest && fastest.speed_to_mw_months != null
          ? `${fastest.speed_to_mw_months.toFixed(0)} mo`
          : null}
      />
      <Stat
        label="Lowest net cost"
        value={lowestCost ? `${lowestCost.parcel_id}` : '—'}
        sub={lowestCost && lowestCost.deal_economics?.net_development_cost_usd?.mid != null
          ? `$${formatNumber(lowestCost.deal_economics.net_development_cost_usd.mid)}`
          : null}
      />
      <Stat
        label="Highest revenue acceleration"
        value={highestRev ? `${highestRev.parcel_id}` : '—'}
        sub={highestRev && highestRev.revenue_acceleration_value_usd
          ? `$${formatNumber(highestRev.revenue_acceleration_value_usd)}`
          : null}
      />
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{label}</p>
      <p className="mt-1 truncate text-lg font-bold text-zinc-900">{value}</p>
      {sub && <p className="text-xs font-mono text-zinc-500">{sub}</p>}
    </div>
  );
}

function KanbanColumn({ stage, parcels, onDrop, draggedId, setDraggedId }) {
  const [isOver, setIsOver] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsOver(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsOver(false);
    const parcelId = e.dataTransfer.getData('text/plain');
    if (parcelId && parcelId !== draggedId) {
      // dragenter from a different column doesn't carry parcelId on
      // some browsers; fall back to draggedId state.
    }
    const finalId = parcelId || draggedId;
    if (finalId) onDrop(finalId);
    setDraggedId(null);
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={() => setIsOver(false)}
      onDrop={handleDrop}
      className={`flex min-h-[300px] flex-col rounded-2xl border-2 ${stage.accent} ${
        isOver ? 'ring-2 ring-zinc-900 ring-offset-2' : ''
      } p-3 transition-all`}
    >
      <header className="mb-3 flex items-center justify-between">
        <h2 className="text-xs font-bold uppercase tracking-wider text-zinc-700">
          {stage.label}
        </h2>
        <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-zinc-600">
          {parcels.length}
        </span>
      </header>
      <div className="space-y-2">
        {parcels.length === 0 ? (
          <p className="rounded-lg border border-dashed border-zinc-300 px-3 py-2 text-center text-xs italic text-zinc-400">
            no parcels
          </p>
        ) : (
          parcels.map((p) => (
            <ParcelCard
              key={p.parcel_id}
              parcel={p}
              onDragStart={() => setDraggedId(p.parcel_id)}
              onDragEnd={() => setDraggedId(null)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function ParcelCard({ parcel, onDragStart, onDragEnd }) {
  const handleDragStart = (e) => {
    e.dataTransfer.setData('text/plain', parcel.parcel_id);
    e.dataTransfer.effectAllowed = 'move';
    onDragStart();
  };

  const score = parcel.composite_score ?? 0;
  const scoreColor =
    score >= 70 ? 'bg-emerald-100 text-emerald-800' :
    score >= 50 ? 'bg-amber-100 text-amber-800' :
    'bg-zinc-100 text-zinc-700';

  const speed = parcel.speed_to_mw_months;
  const revAccel = parcel.revenue_acceleration_value_usd;

  const topRisk = parcel.power_solution_matrix?.data_gaps?.[0]
    || parcel.cooling_solution_matrix?.data_gaps?.[0]
    || '—';
  const truncatedRisk = topRisk.length > 50 ? `${topRisk.slice(0, 47)}…` : topRisk;

  return (
    <Link
      to={`/land-intelligence/parcel/${encodeURIComponent(parcel.parcel_id)}`}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={onDragEnd}
      className="block cursor-grab rounded-lg border border-zinc-200 bg-white p-3 shadow-sm transition-all hover:shadow-md active:cursor-grabbing"
    >
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <p className="truncate text-xs font-mono font-semibold text-zinc-900">{parcel.parcel_id}</p>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${scoreColor}`}>
          {score.toFixed(0)}
        </span>
      </div>
      <p className="mb-2 text-xs text-zinc-600">
        {parcel.county}, {parcel.state} · {parcel.acreage?.toFixed(0) ?? '—'} ac
      </p>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <p className="text-zinc-500">Speed</p>
          <p className="font-mono font-semibold text-zinc-900">
            {speed != null ? `${speed.toFixed(0)} mo` : '—'}
          </p>
        </div>
        <div>
          <p className="text-zinc-500">Rev accel</p>
          <p className="font-mono font-semibold text-zinc-900">
            {revAccel ? `$${formatNumber(revAccel)}` : '—'}
          </p>
        </div>
      </div>
      <p className="mt-2 truncate text-xs italic text-amber-800" title={topRisk}>
        ⚠ {truncatedRisk}
      </p>
    </Link>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-zinc-300 bg-white p-12 text-center">
      <p className="text-sm uppercase tracking-wider text-zinc-500">No parcels yet</p>
      <h2 className="mt-2 text-2xl font-bold text-zinc-900">
        Add a parcel to start the pipeline
      </h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-zinc-600">
        Each parcel runs the full Power↔Water convergence loop and Deal
        Economics on save, surfacing speed-to-MW and revenue-acceleration vs
        the next-best market.
      </p>
      <button
        type="button"
        onClick={onAdd}
        className="mt-6 rounded-xl bg-zinc-900 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-zinc-700"
      >
        + Add your first parcel
      </button>
    </div>
  );
}

function AddParcelModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    parcel_id: '',
    state: 'TX',
    county: '',
    municipality: '',
    latitude: '32.78',
    longitude: '-96.80',
    acreage: '200',
    target_mw: '150',
    pipeline_stage: 'screening',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        parcel_id: form.parcel_id || undefined,
        state: form.state.toUpperCase(),
        county: form.county,
        municipality: form.municipality || undefined,
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude),
        acreage: parseFloat(form.acreage),
        target_mw: parseFloat(form.target_mw),
        pipeline_stage: form.pipeline_stage,
      };
      const resp = await fetch(`${API_BASE}/land/parcels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`Create failed: ${resp.status} ${text.slice(0, 200)}`);
      }
      onCreated();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-zinc-900/40 p-6 pt-16">
      <div className="w-full max-w-2xl rounded-2xl border border-zinc-200 bg-white shadow-xl">
        <header className="flex items-center justify-between border-b border-zinc-200 px-5 py-4">
          <h2 className="text-lg font-bold text-zinc-900">Add parcel</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-zinc-500 hover:bg-zinc-100"
          >
            ✕
          </button>
        </header>
        <form onSubmit={submit} className="space-y-4 p-5">
          <p className="text-sm text-zinc-600">
            Submitting runs Power Agent → Water Agent → convergence (max 3
            iterations) → Deal Economics with revenue acceleration vs the
            nearest reference market. Typical analysis: 5-15 seconds.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Parcel ID (optional)" value={form.parcel_id} onChange={update('parcel_id')} placeholder="auto-generated if blank" />
            <Field label="Pipeline stage" value={form.pipeline_stage} onChange={update('pipeline_stage')} list={['discovered', 'screening', 'qualified', 'diligence']} />
            <Field label="State (2-letter)" value={form.state} onChange={update('state')} required maxLength={2} />
            <Field label="County" value={form.county} onChange={update('county')} required />
            <Field label="Municipality (optional)" value={form.municipality} onChange={update('municipality')} />
            <Field label="Acreage" type="number" value={form.acreage} onChange={update('acreage')} required step="1" />
            <Field label="Latitude" type="number" value={form.latitude} onChange={update('latitude')} required step="0.0001" />
            <Field label="Longitude" type="number" value={form.longitude} onChange={update('longitude')} required step="0.0001" />
            <Field label="Target MW" type="number" value={form.target_mw} onChange={update('target_mw')} required step="1" />
          </div>
          {error && (
            <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 border-t border-zinc-200 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-zinc-300 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-xl bg-zinc-900 px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-zinc-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? 'Analyzing…' : 'Create + analyze'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder, required, step, maxLength, list }) {
  const listId = list ? `${label.replace(/\W+/g, '-')}-list` : undefined;
  return (
    <label className="flex flex-col gap-1 text-xs font-semibold text-zinc-600">
      {label}
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        step={step}
        maxLength={maxLength}
        list={listId}
        className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 outline-none focus:border-zinc-500"
      />
      {list && (
        <datalist id={listId}>
          {list.map((opt) => <option key={opt} value={opt} />)}
        </datalist>
      )}
    </label>
  );
}

function formatNumber(n) {
  if (n == null) return '—';
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toFixed(0);
}
